import { BaseElement, FormAssociated, css, html, define, upgradeProperties, emitValueEvents, adoptStyles } from '../../../core/index.js';

const TAG_NAME = 'omni-select';

const styles = css`
:host {
  --omni-select-bg: var(--form-control-bg, #fff);
  --omni-select-border: var(--form-control-border, #8f8f9d);
  --omni-select-border-hover: var(--form-control-border-hover, #9ea8b3);
  --omni-select-border-focus: var(--form-control-border-focus, #3a7bce);
  --omni-select-text: var(--form-control-text, #0f1721);
  --omni-select-muted: var(--form-control-muted, #6b7380);
  --omni-select-pill-bg: var(--form-control-pill-bg, #edf1f5);
  --omni-select-pill-text: var(--form-control-pill-text, #303b49);
  --omni-select-pill-remove: var(--form-control-pill-remove, #4a5260);
  --omni-select-option-hover: color-mix(in srgb, var(--omni-select-border-focus) 12%, transparent);
  --omni-select-option-active: color-mix(in srgb, var(--omni-select-border-focus) 18%, transparent);
  --omni-select-shadow: 0 6px 24px rgba(15, 35, 50, 0.12);
  --omni-select-sheet-top: 0px;
  --omni-select-sheet-side-gap: 0px;
  --omni-select-sheet-bottom: 0px;
  --omni-select-dropdown-left: 0px;
  --omni-select-dropdown-top: 0px;
  --omni-select-dropdown-bottom: auto;
  --omni-select-dropdown-width: 100%;
  --omni-select-dropdown-max-height: 18rem;
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  vertical-align: middle;
  width: var(--omni-select-width, auto);
  max-width: 100%;
  min-height: var(--omni-select-min-height, auto);
  padding: var(--omni-select-padding, 2.5px 2px);
  border: var(--omni-select-border-width, 1px) solid var(--omni-select-border);
  border-radius: var(--omni-select-radius, 2px);
  background: var(--omni-select-bg);
  color: var(--omni-select-text);
  font-family: MS Shell Dlg, sans-serif;
  font-weight: normal;
  font-size: 13.3333px;
  line-height: normal;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-clip: padding-box;
}

:host(:not([disabled])) {
  cursor: pointer;
}

:host(:not([disabled])):hover {
  border-color: var(--omni-select-border-hover);
}

:host(:focus-within),
:host([open]) {
  border-color: var(--omni-select-border-focus);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--omni-select-border-focus) 25%, transparent);
}

:host([disabled]) {
  opacity: 0.6;
  cursor: not-allowed;
}

.control {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 1em;
  overflow: hidden;
  cursor: inherit;
  background: transparent;
}

.value-display {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-wrap: wrap;
  min-height: 1em;
  min-width: 0;
  overflow: hidden;
}

:host(:not([open])) .value-display {
  max-height: 1.6em;
}

.value-text {
  color: var(--omni-select-text);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.value-text.placeholder {
  color: var(--omni-select-muted);
}

.pills {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  flex: 1 1 auto;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  max-width: 100%;
  border-radius: 999px;
  background: var(--omni-select-pill-bg);
  color: var(--omni-select-pill-text);
  padding: 0.05rem 0.25rem;
  font-size: 0.65rem;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
}

.pill button {
  all: unset;
  font-size: 0.9em;
  flex: 0 0 auto;
  cursor: pointer;
  color: var(--omni-select-pill-remove);
}

.chevron {
  width: 0.35rem;
  height: 0.35rem;
  border-right: 1px solid var(--omni-select-muted);
  border-bottom: 1px solid var(--omni-select-muted);
  transform: rotate(45deg);
  transition: transform 0.15s ease;
  margin-left: auto;
  margin-top: 0;
  margin-right: 4px;
  margin-bottom: 2px;
  flex-shrink: 0;
  pointer-events: none;
}

:host([open]) .chevron {
  transform: rotate(-135deg);
}

.dropdown {
  position: fixed;
  top: var(--omni-select-dropdown-top);
  left: var(--omni-select-dropdown-left);
  width: var(--omni-select-dropdown-width);
  border-radius: var(--omni-select-radius, 0.35rem);
  border: 1px solid var(--omni-select-border);
  background: var(--omni-select-bg);
  box-shadow: var(--omni-select-shadow);
  z-index: var(--omni-select-z-index, 100000);
  display: flex;
  flex-direction: column;
  max-height: var(--omni-select-dropdown-max-height);
  overflow: hidden;
}

:host([dropup]:not([mobile-sheet])) .dropdown {
  top: auto;
  bottom: var(--omni-select-dropdown-bottom);
}

:host([mobile-sheet]) .dropdown {
  position: fixed;
  top: calc(var(--omni-select-sheet-top) + env(safe-area-inset-top, 0px));
  left: calc(var(--omni-select-sheet-side-gap) + env(safe-area-inset-left, 0px));
  right: calc(var(--omni-select-sheet-side-gap) + env(safe-area-inset-right, 0px));
  bottom: calc(var(--omni-select-sheet-bottom) + env(safe-area-inset-bottom, 0px));
  width: auto;
  border: none;
  max-height: none;
  border-radius: 0;
  z-index: var(--omni-select-mobile-sheet-z-index, var(--omni-select-z-index, 100000));
}

:host(:not([open])) .dropdown {
  display: none;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.25rem;
  border-bottom: 1px solid color-mix(in srgb, var(--omni-select-border) 65%, transparent);
}

.search-row input {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid var(--omni-select-border);
  border-radius: 3px;
  padding: 0.2rem 0.35rem;
  font: inherit;
}

.sheet-close {
  display: none;
  flex: 0 0 auto;
  border: 1px solid var(--omni-select-border);
  border-radius: 0.45rem;
  background: color-mix(in srgb, var(--omni-select-bg) 90%, #fff 10%);
  color: var(--omni-select-text);
  padding: 0.42rem 0.62rem;
  font: inherit;
  cursor: pointer;
}

:host([mobile-sheet]) .search-row {
  padding: 0.7rem;
}

:host([mobile-sheet]) .search-row input {
  flex: 1 1 auto;
  min-height: 2.75rem;
  border-radius: 0.55rem;
  padding: 0.55rem 0.75rem;
  font-size: 16px;
}

:host([mobile-sheet]) .sheet-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  font-size: 0.95rem;
}

.options {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.option {
  padding: 0.3rem 0.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.option:hover {
  background: var(--omni-select-option-hover);
}

.option.selected {
  background: var(--omni-select-option-active);
  font-weight: 600;
}

.option.active:not(.selected) {
  background: var(--omni-select-option-hover);
}

.option.create {
  font-style: italic;
}

.option.disabled {
  cursor: not-allowed;
  color: var(--omni-select-muted);
  opacity: 0.7;
}

.option small {
  font-size: 0.75rem;
  color: var(--omni-select-muted);
}

:host([mobile-sheet]) .option {
  min-height: 2.65rem;
  padding: 0.65rem 0.8rem;
  font-size: 1rem;
}

:host([mobile-sheet]) .option small {
  font-size: 0.86rem;
}

.empty-state {
  padding: 0.75rem;
  text-align: center;
  color: var(--omni-select-muted);
  font-size: 0.9rem;
}

slot { display: none; }
::slotted(option) {
  display: none !important;
}
`;

// Standalone stylesheet for the body-level portal used in mobile-sheet mode.
// These styles intentionally duplicate the mobile-sheet subset of `styles` so
// the portal shadow root doesn't rely on the host element's shadow DOM cascade.
const _portalStyles = css`
:host {
  display: flex;
  flex-direction: column;
  position: fixed;
  top: calc(var(--omni-select-sheet-top, 0px) + env(safe-area-inset-top, 0px));
  left: calc(var(--omni-select-sheet-side-gap, 0px) + env(safe-area-inset-left, 0px));
  right: calc(var(--omni-select-sheet-side-gap, 0px) + env(safe-area-inset-right, 0px));
  bottom: calc(var(--omni-select-sheet-bottom, 0px) + env(safe-area-inset-bottom, 0px));
  z-index: var(--omni-select-mobile-sheet-z-index, var(--omni-select-z-index, 100000));
  overflow: hidden;
  font-family: MS Shell Dlg, sans-serif;
  font-weight: normal;
  font-size: 13.3333px;
  line-height: normal;
  color: var(--omni-select-text, #0f1721);
}

.dropdown {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--omni-select-bg, #fff);
  overflow: hidden;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem;
  border-bottom: 1px solid color-mix(in srgb, var(--omni-select-border, #8f8f9d) 65%, transparent);
}

.search-row input {
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 2.75rem;
  border: 1px solid var(--omni-select-border, #8f8f9d);
  border-radius: 0.55rem;
  padding: 0.55rem 0.75rem;
  font: inherit;
  font-size: 16px;
  background: var(--omni-select-bg, #fff);
  color: var(--omni-select-text, #0f1721);
  outline: none;
}

.search-row input:focus {
  border-color: var(--omni-select-border-focus, #3a7bce);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--omni-select-border-focus, #3a7bce) 25%, transparent);
}

.sheet-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 2.75rem;
  border: 1px solid var(--omni-select-border, #8f8f9d);
  border-radius: 0.45rem;
  background: color-mix(in srgb, var(--omni-select-bg, #fff) 90%, #fff 10%);
  color: var(--omni-select-text, #0f1721);
  padding: 0.42rem 0.62rem;
  font: inherit;
  font-size: 0.95rem;
  cursor: pointer;
}

.options {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.option {
  min-height: 2.65rem;
  padding: 0.65rem 0.8rem;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.option:hover {
  background: var(--omni-select-option-hover, color-mix(in srgb, #3a7bce 12%, transparent));
}

.option.selected {
  background: var(--omni-select-option-active, color-mix(in srgb, #3a7bce 18%, transparent));
  font-weight: 600;
}

.option.active:not(.selected) {
  background: var(--omni-select-option-hover, color-mix(in srgb, #3a7bce 12%, transparent));
}

.option.create {
  font-style: italic;
}

.option.disabled {
  cursor: not-allowed;
  color: var(--omni-select-muted, #6b7380);
  opacity: 0.7;
}

.option small {
  font-size: 0.86rem;
  color: var(--omni-select-muted, #6b7380);
}

.empty-state {
  padding: 0.75rem;
  text-align: center;
  color: var(--omni-select-muted, #6b7380);
  font-size: 0.9rem;
}
`;

const template = html`
  <div class="control" part="control" role="combobox" tabindex="0" aria-haspopup="listbox" aria-expanded="false">
    <div class="value-display" part="value">
      <span class="value-text placeholder"></span>
      <div class="pills" part="pills" hidden></div>
    </div>
    <span class="chevron" part="chevron"></span>
  </div>
  <div class="dropdown" part="dropdown">
    <div class="search-row">
      <input type="text" placeholder="Search..." aria-label="Search options" autocomplete="off">
      <button type="button" class="sheet-close" part="sheet-close" aria-label="Done">Done</button>
    </div>
    <ul class="options" part="options" role="listbox"></ul>
    <div class="empty-state" part="empty" hidden>No matches</div>
  </div>
  <slot></slot>
`;

const parseMultiAttribute = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === 'string') {
    return val.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const normalizeToken = (val) => String(val ?? '').trim().toLowerCase();
const MOBILE_SHEET_BREAKPOINT = 768;
const MIN_ANCHORED_DROPDOWN_HEIGHT = 96;
const MAX_ANCHORED_DROPDOWN_HEIGHT = 520;
const MIN_SPACE_FOR_BELOW_PLACEMENT = 180;
const ANCHORED_EDGE_GAP = 8;

class OmniSelect extends FormAssociated(BaseElement) {
  static shadowStyleSheets = [styles];
  static formAssociated = super.formAssociated;
  static get observedAttributes() {
    return ['open', 'disabled', 'placeholder', 'multiple', 'value', 'name', 'allow-custom', 'add-text'];
  }

  constructor() {
    super();
    upgradeProperties(this, ['value', 'placeholder', 'disabled', 'multiple', 'open', 'name', 'allowCustom', 'addText']);
    this._options = [];
    this._selectedValues = new Set();
    this._visibleOptions = [];
    this._activeOptionIndex = -1;
    this._typeaheadBuffer = '';
    this._typeaheadTimestamp = 0;
    this._tabIndexManaged = false;
    this._blurTimeout = null;
    this._isSyncingValueAttribute = false;
    this._portalEl = null;
    this._dropdownNextSibling = null;

    this._onDocumentPointer = this._onDocumentPointer.bind(this);
    this._onViewportChange = this._onViewportChange.bind(this);
    this._handleSlotChange = this._handleSlotChange.bind(this);
    this._handleFocusOut = this._handleFocusOut.bind(this);
    this._handleControlPointerDown = (ev) => {
      if (this.disabled) return;
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      ev.preventDefault();
      this._control?.focus();
    };
    this._handleHostPointerDown = (ev) => {
      if (this.disabled) return;
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      const actualTarget = ev.composedPath?.()[0] ?? ev.target;
      if (actualTarget !== this) return;
      ev.preventDefault();
      this._control?.focus();
    };
    this._handleHostClick = (ev) => {
      if (this.disabled) return;
      const actualTarget = ev.composedPath?.()[0] ?? ev.target;
      if (actualTarget !== this) return;
      this.toggle();
    };
    this._handleFocusIn = () => {
      if (this._blurTimeout) {
        clearTimeout(this._blurTimeout);
        this._blurTimeout = null;
      }
    };
    this._handleHostFocus = (ev) => {
      if (ev.target === this && !this.disabled) {
        this._control?.focus();
      }
    };

    this._root.append(template.cloneNode(true));
    this._control = this._root.querySelector('.control');
    this._valueText = this._root.querySelector('.value-text');
    this._pills = this._root.querySelector('.pills');
    this._dropdown = this._root.querySelector('.dropdown');
    this._searchInput = this._root.querySelector('.search-row input');
    this._sheetClose = this._root.querySelector('.sheet-close');
    this._optionsList = this._root.querySelector('.options');
    this._emptyState = this._root.querySelector('.empty-state');
    this._slot = this._root.querySelector('slot');

    this._slot.addEventListener('slotchange', this._handleSlotChange);
    this._control.addEventListener('pointerdown', this._handleControlPointerDown);
    this._control.addEventListener('click', () => this.toggle());
    this.addEventListener('pointerdown', this._handleHostPointerDown);
    this.addEventListener('click', this._handleHostClick);
    this._control.addEventListener('keydown', (ev) => this._onControlKeyDown(ev));
    this._searchInput.addEventListener('input', () => this._renderOptions());
    this._searchInput.addEventListener('keydown', (ev) => this._onSearchKeyDown(ev));
    this._sheetClose.addEventListener('click', () => {
      this.hide();
      this._control.focus();
    });
    this._optionsList.addEventListener('click', (ev) => this._onOptionClick(ev));
    this.addEventListener('focusout', this._handleFocusOut);
    this.addEventListener('focusin', this._handleFocusIn);
    this.addEventListener('focus', this._handleHostFocus);
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (!this._tabIndexManaged) {
      this.tabIndex = this.disabled ? -1 : 0;
      this._tabIndexManaged = true;
    }
    document.addEventListener('pointerdown', this._onDocumentPointer);
    document.addEventListener('scroll', this._onViewportChange, true);
    window.addEventListener('resize', this._onViewportChange);
    window.visualViewport?.addEventListener('resize', this._onViewportChange);
    window.visualViewport?.addEventListener('scroll', this._onViewportChange);
    this._ensureHiddenInputsAttach();
    this._handleSlotChange();
  }

  focus(options) {
    this._control?.focus(options);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocumentPointer);
    document.removeEventListener('scroll', this._onViewportChange, true);
    window.removeEventListener('resize', this._onViewportChange);
    window.visualViewport?.removeEventListener('resize', this._onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this._onViewportChange);
    this._teardownHiddenInputs();
    this._control?.removeEventListener('pointerdown', this._handleControlPointerDown);
    this.removeEventListener('pointerdown', this._handleHostPointerDown);
    this.removeEventListener('click', this._handleHostClick);
    this.removeEventListener('focus', this._handleHostFocus);
    this.removeEventListener('focusin', this._handleFocusIn);
    this.removeEventListener('focusout', this._handleFocusOut);
    if (this._blurTimeout) {
      clearTimeout(this._blurTimeout);
      this._blurTimeout = null;
    }
    this._unmountPortal();
    this._clearMobileSheetMetrics();
    this.removeAttribute('mobile-sheet');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    switch (name) {
      case 'open':
        this._syncOpenState();
        break;
      case 'disabled':
        this._syncDisabledState();
        break;
      case 'placeholder':
        this._renderValueSummary();
        break;
      case 'multiple':
        this._handleMultipleChange();
        break;
      case 'value':
        if (this._isSyncingValueAttribute) break;
        if (newValue !== oldValue) {
          this._applyValueFromAttribute(newValue);
        }
        break;
      case 'name':
        this._syncFormValue();
        break;
      case 'allow-custom':
      case 'add-text':
        this._renderOptions();
        break;
      default:
        break;
    }
  }

  get placeholder() {
    return this.getAttribute('placeholder') ?? 'Select option(s)';
  }

  set placeholder(val) {
    if (val == null) this.removeAttribute('placeholder');
    else this.setAttribute('placeholder', val);
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(v) {
    v ? this.setAttribute('disabled', '') : this.removeAttribute('disabled');
  }

  get multiple() {
    return this.hasAttribute('multiple');
  }

  set multiple(v) {
    v ? this.setAttribute('multiple', '') : this.removeAttribute('multiple');
  }

  get allowCustom() {
    return this.hasAttribute('allow-custom');
  }

  set allowCustom(v) {
    v ? this.setAttribute('allow-custom', '') : this.removeAttribute('allow-custom');
  }

  get addText() {
    return this.getAttribute('add-text') ?? 'Add "{value}"';
  }

  set addText(v) {
    if (v == null) this.removeAttribute('add-text');
    else this.setAttribute('add-text', String(v));
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(v) {
    v ? this.setAttribute('open', '') : this.removeAttribute('open');
  }

  get value() {
    if (this.multiple) {
      return Array.from(this._selectedValues);
    }
    const iter = this._selectedValues.values().next();
    return iter.done ? '' : iter.value;
  }

  set value(v) {
    if (this.multiple) {
      const values = parseMultiAttribute(v);
      this._selectedValues = new Set(values);
      this._syncOptionSelection(true);
    } else {
      const str = v == null ? '' : String(v);
      this._selectedValues = str ? new Set([str]) : new Set();
      this._syncOptionSelection(true);
      this._syncValueAttribute(str);
    }
    this._renderValueSummary();
    this._renderOptions();
    this._syncFormValue();
  }

  toggle() {
    if (this.disabled) return;
    this.open = !this.open;
  }

  show() {
    if (this.disabled) return;
    this.open = true;
  }

  hide() {
    this.open = false;
    this._clearTypeahead();
  }

  _handleSlotChange() {
    const optionEls = Array.from(this.querySelectorAll('option'));
    this._options = optionEls.map((opt) => ({
      el: opt,
      value: opt.value ?? opt.textContent ?? '',
      label: opt.label ?? opt.textContent?.trim() ?? opt.value ?? '',
      disabled: opt.disabled,
      hint: opt.dataset?.hint ?? '',
    }));

    const preselected = optionEls.filter((o) => o.selected).map((o) => o.value ?? o.textContent ?? '');
    if (preselected.length) {
      this._selectedValues = new Set(this.multiple ? preselected : [preselected[0]]);
    }

    this._syncOptionSelection(true);
    this._renderOptions();
    this._renderValueSummary();
    this._syncFormValue();
  }

  _syncOpenState() {
    const isOpen = this.open && !this.disabled;
    this._control.setAttribute('aria-expanded', String(isOpen));
    this._syncPopoverLayout();
    if (!isOpen) {
      this._clearMobileSheetMetrics();
      this._clearAnchoredPopoverMetrics();
      this.removeAttribute('mobile-sheet');
      return;
    }

    if (isOpen) {
      this._searchInput.value = '';
      this._renderOptions();
      requestAnimationFrame(() => {
        this._control.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this._syncPopoverLayout();
        this._searchInput.focus();
        if (!this._searchInput.value) {
          this._searchInput.select();
        } else {
          const len = this._searchInput.value.length;
          this._searchInput.setSelectionRange(len, len);
        }
      });
    }
  }

  _syncDisabledState() {
    const disabled = this.disabled;
    this._control.tabIndex = disabled ? -1 : 0;
    if (this._tabIndexManaged) {
      this.tabIndex = disabled ? -1 : 0;
    }
    if (disabled) this.hide();
    this._syncFormValue();
  }

  _onViewportChange() {
    if (!this.open) return;
    this._syncPopoverLayout();
  }

  _shouldUseMobileSheet() {
    if (typeof window === 'undefined') return false;
    const width = window.visualViewport?.width ?? window.innerWidth;
    return width <= MOBILE_SHEET_BREAKPOINT;
  }

  _syncPopoverLayout() {
    if (!this.open || this.disabled) {
      this._unmountPortal();
      this.removeAttribute('mobile-sheet');
      this._clearMobileSheetMetrics();
      this._clearAnchoredPopoverMetrics();
      return;
    }

    const useSheet = this.open && !this.disabled && this._shouldUseMobileSheet();
    this.toggleAttribute('mobile-sheet', useSheet);

    if (useSheet) {
      this._clearAnchoredPopoverMetrics();
      this._mountPortal();
      return;
    }

    this._unmountPortal();
    this._clearMobileSheetMetrics();
    this._syncAnchoredPopoverMetrics();
  }

  _syncAnchoredPopoverMetrics() {
    const rect = this.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportRight = viewportLeft + viewportWidth;
    const viewportBottom = viewportTop + viewportHeight;

    const spaceBelow = Math.max(0, viewportBottom - rect.bottom - ANCHORED_EDGE_GAP);
    const spaceAbove = Math.max(0, rect.top - viewportTop - ANCHORED_EDGE_GAP);
    const openUp = spaceBelow < MIN_SPACE_FOR_BELOW_PLACEMENT && spaceAbove > spaceBelow;
    const available = openUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      MIN_ANCHORED_DROPDOWN_HEIGHT,
      Math.min(MAX_ANCHORED_DROPDOWN_HEIGHT, Math.round(available))
    );

    const minLeft = viewportLeft + ANCHORED_EDGE_GAP;
    const maxRight = viewportRight - ANCHORED_EDGE_GAP;
    const maxWidth = Math.max(0, maxRight - minLeft);
    const width = Math.max(0, Math.min(rect.width, maxWidth));
    const left = Math.min(Math.max(rect.left, minLeft), maxRight - width);
    const popoverGap = parseFloat(getComputedStyle(this).getPropertyValue('--omni-select-popover-gap')) || 4;
    const top = rect.bottom + popoverGap;
    const bottom = Math.max(0, window.innerHeight - rect.top + popoverGap);

    this.toggleAttribute('dropup', openUp);
    this.style.setProperty('--omni-select-dropdown-left', `${Math.round(left)}px`);
    this.style.setProperty('--omni-select-dropdown-top', `${Math.round(top)}px`);
    this.style.setProperty('--omni-select-dropdown-bottom', `${Math.round(bottom)}px`);
    this.style.setProperty('--omni-select-dropdown-width', `${Math.round(width)}px`);
    this.style.setProperty('--omni-select-dropdown-max-height', `${maxHeight}px`);
  }

  _syncMobileSheetMetrics() {
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const keyboardInset = viewport ? Math.max(0, window.innerHeight - (viewport.height + viewport.offsetTop)) : 0;
    const top = `${Math.max(0, Math.round(viewportTop))}px`;
    const bottom = `${Math.max(0, Math.round(keyboardInset))}px`;
    const target = this._portalEl ?? this;
    target.style.setProperty('--omni-select-sheet-top', top);
    target.style.setProperty('--omni-select-sheet-bottom', bottom);
    target.style.setProperty('--omni-select-sheet-side-gap', '0px');
  }

  _clearAnchoredPopoverMetrics() {
    this.removeAttribute('dropup');
    this.style.removeProperty('--omni-select-dropdown-left');
    this.style.removeProperty('--omni-select-dropdown-top');
    this.style.removeProperty('--omni-select-dropdown-bottom');
    this.style.removeProperty('--omni-select-dropdown-width');
    this.style.removeProperty('--omni-select-dropdown-max-height');
  }

  _clearMobileSheetMetrics() {
    this.style.removeProperty('--omni-select-sheet-top');
    this.style.removeProperty('--omni-select-sheet-bottom');
    this.style.removeProperty('--omni-select-sheet-side-gap');
    if (this._portalEl) {
      this._portalEl.style.removeProperty('--omni-select-sheet-top');
      this._portalEl.style.removeProperty('--omni-select-sheet-bottom');
      this._portalEl.style.removeProperty('--omni-select-sheet-side-gap');
    }
  }

  _mountPortal() {
    if (this._portalEl) {
      this._syncMobileSheetMetrics();
      return;
    }
    this._portalEl = document.createElement('div');
    const portalRoot = this._portalEl.attachShadow({ mode: 'open' });
    adoptStyles(portalRoot, [_portalStyles]);

    // Copy CSS custom properties so consumer theming flows through.
    const computed = getComputedStyle(this);
    [
      '--omni-select-bg', '--omni-select-border', '--omni-select-border-focus',
      '--omni-select-text', '--omni-select-muted', '--omni-select-option-hover',
      '--omni-select-option-active', '--omni-select-shadow',
      '--omni-select-z-index', '--omni-select-mobile-sheet-z-index',
    ].forEach((prop) => {
      const val = computed.getPropertyValue(prop).trim();
      if (val) this._portalEl.style.setProperty(prop, val);
    });

    this._syncMobileSheetMetrics();

    // Move the dropdown out of the shadow root into the portal.
    this._dropdownNextSibling = this._dropdown.nextSibling;
    portalRoot.appendChild(this._dropdown);

    document.body.appendChild(this._portalEl);
  }

  _unmountPortal() {
    if (!this._portalEl) return;
    // Restore the dropdown to its original position in the shadow root.
    this._root.insertBefore(this._dropdown, this._dropdownNextSibling);
    this._dropdownNextSibling = null;
    this._portalEl.remove();
    this._portalEl = null;
  }

  _handleMultipleChange() {
    if (!this.multiple && this._selectedValues.size > 1) {
      const firstIter = this._selectedValues.values().next();
      const first = firstIter.done ? undefined : firstIter.value;
      this._selectedValues = first ? new Set([first]) : new Set();
    }
    this._renderValueSummary();
    this._renderOptions();
    this._syncFormValue();
  }

  _applyValueFromAttribute(attrValue) {
    if (this.multiple) {
      this._selectedValues = new Set(parseMultiAttribute(attrValue));
    } else {
      this._selectedValues = attrValue ? new Set([attrValue]) : new Set();
    }
    this._syncOptionSelection(true);
    this._renderValueSummary();
    this._renderOptions();
    this._syncFormValue();
  }

  _findExactOption(rawValue) {
    const token = normalizeToken(rawValue);
    if (!token) return null;
    return this._options.find((opt) => normalizeToken(opt.value) === token || normalizeToken(opt.label) === token) ?? null;
  }

  _formatAddText(value) {
    const template = String(this.addText ?? 'Add "{value}"');
    if (template.includes('{value}')) return template.replaceAll('{value}', value);
    if (template.includes('%s')) return template.replaceAll('%s', value);
    return `${template} "${value}"`;
  }

  _addCustomValue(rawValue) {
    if (!this.allowCustom || this.disabled) return;
    const value = String(rawValue ?? '').trim();
    if (!value) return;
    if (this.multiple && value.includes(',')) return;

    const existing = this._findExactOption(value);
    const nextValue = existing?.value ?? value;

    if (!existing) {
      const option = document.createElement('option');
      option.value = nextValue;
      option.textContent = nextValue;
      option.selected = true;
      option.dataset.custom = 'true';
      this.append(option);
      this._options.push({
        el: option,
        value: nextValue,
        label: nextValue,
        disabled: false,
        hint: '',
      });
    }

    this._toggleValue(nextValue, true);

    if (this.multiple) {
      this._searchInput.value = '';
      this._renderOptions();
      this._searchInput.focus();
    }
  }

  _renderOptions({ preserveScroll = false, preserveActiveValue = null, suppressActiveScroll = false } = {}) {
    if (!this._optionsList) return;
    const previousScrollTop = preserveScroll ? this._optionsList.scrollTop : 0;
    const rawNeedle = this._searchInput.value ?? '';
    const needleValue = rawNeedle.trim();
    const needle = needleValue.toLowerCase();
    this._optionsList.innerHTML = '';
    this._visibleOptions = [];
    let visible = 0;

    for (const opt of this._options) {
      const text = `${opt.label} ${opt.value}`.toLowerCase();
      if (needle && !text.includes(needle)) continue;
      visible++;
      const li = document.createElement('li');
      li.className = 'option' + (this._selectedValues.has(opt.value) ? ' selected' : '') + (opt.disabled ? ' disabled' : '');
      li.dataset.value = opt.value;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(this._selectedValues.has(opt.value)));
      li.textContent = opt.label;
      if (opt.hint) {
        const hint = document.createElement('small');
        hint.textContent = opt.hint;
        li.appendChild(hint);
      }
      this._optionsList.appendChild(li);
      this._visibleOptions.push({ option: opt, li });
    }

    if (this.allowCustom && needleValue) {
      const hasExactMatch = !!this._findExactOption(needleValue);
      const canCreate = !this.multiple || !needleValue.includes(',');
      if (!hasExactMatch && canCreate) {
        visible++;
        const li = document.createElement('li');
        li.className = 'option create';
        li.dataset.create = 'true';
        li.dataset.value = needleValue;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.textContent = this._formatAddText(needleValue);
        this._optionsList.appendChild(li);
        this._visibleOptions.push({
          option: null,
          li,
          isCreate: true,
          createValue: needleValue,
        });
      }
    }

    this._emptyState.hidden = visible !== 0;
    if (this.open) {
      if (preserveActiveValue != null) {
        const idx = this._visibleOptions.findIndex((entry) => entry.option?.value === preserveActiveValue);
        if (idx >= 0) this._setActiveOption(idx, { scrollIntoView: !suppressActiveScroll });
        else this._resetActiveOption({ scrollIntoView: !suppressActiveScroll });
      } else {
        this._resetActiveOption({ scrollIntoView: !suppressActiveScroll });
      }
    }
    if (preserveScroll) {
      this._optionsList.scrollTop = previousScrollTop;
    }
  }

  _renderValueSummary() {
    const placeholder = this.placeholder;
    const values = Array.from(this._selectedValues);

    if (this.multiple) {
      this._pills.hidden = values.length === 0;
      this._pills.innerHTML = '';
      if (values.length === 0) {
        this._valueText.textContent = placeholder;
        this._valueText.classList.add('placeholder');
      } else {
        this._valueText.textContent = '';
        this._valueText.classList.remove('placeholder');
        for (const val of values) {
          const data = this._options.find((o) => o.value === val);
          const pill = document.createElement('span');
          pill.className = 'pill';
          pill.textContent = data?.label ?? val;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.setAttribute('aria-label', `Remove ${data?.label ?? val}`);
          btn.textContent = '×';
          btn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            this._toggleValue(val, false);
          });
          pill.appendChild(btn);
          this._pills.appendChild(pill);
        }
      }
    } else {
      this._pills.hidden = true;
      const val = values[0];
      if (val) {
        const option = this._options.find((o) => o.value === val);
        this._valueText.textContent = option?.label ?? val;
        this._valueText.classList.remove('placeholder');
      } else {
        this._valueText.textContent = placeholder;
        this._valueText.classList.add('placeholder');
      }
    }
  }

  _toggleValue(value, force) {
    if (this.disabled) return;
    const exists = this._selectedValues.has(value);
    const shouldSelect = force ?? !exists;

    if (!this.multiple) {
      this._selectedValues = shouldSelect ? new Set([value]) : new Set();
      this.hide();
    } else {
      if (shouldSelect) this._selectedValues.add(value);
      else this._selectedValues.delete(value);
    }

    this._syncOptionSelection();
    this._renderValueSummary();
    if (this.multiple && this.open) {
      this._renderOptions({
        preserveScroll: true,
        preserveActiveValue: value,
        suppressActiveScroll: true,
      });
    } else {
      this._renderOptions();
    }
    this._syncFormValue();
    this._emitChange();
  }

  _syncOptionSelection(skipAttr = false) {
    for (const opt of this._options) {
      const selected = this._selectedValues.has(opt.value);
      opt.el.selected = selected;
      if (selected) opt.el.setAttribute('selected', '');
      else opt.el.removeAttribute('selected');
    }

    if (skipAttr) return;
    if (this.multiple) {
      const joined = Array.from(this._selectedValues).join(',');
      this._syncValueAttribute(joined);
    } else {
      const iter = this._selectedValues.values().next();
      const val = iter.done ? '' : iter.value;
      this._syncValueAttribute(val);
    }
  }

  _syncValueAttribute(value) {
    this._isSyncingValueAttribute = true;
    try {
      if (value) this.setAttribute('value', value);
      else this.removeAttribute('value');
    } finally {
      this._isSyncingValueAttribute = false;
    }
  }

  _syncFormValue() {
    if (this._internals) {
      if (!this.name || this.disabled) {
        this._internals.setFormValue(null);
        return;
      }
      if (this.multiple) {
        if (this._selectedValues.size === 0) {
          this._internals.setFormValue(null);
        } else {
          const fd = new FormData();
          for (const val of this._selectedValues) fd.append(this.name, val);
          this._internals.setFormValue(fd);
        }
      } else {
        const iter = this._selectedValues.values().next();
        const val = iter.done ? '' : iter.value;
        this._internals.setFormValue(val ?? '');
      }
    } else {
      this._syncHiddenInputs();
    }
  }

  _ensureHiddenInputsAttach() {
    if (this._internals) return;
    if (!this._hiddenInputsContainer) {
      this._hiddenInputsContainer = document.createElement('span');
      this._hiddenInputsContainer.hidden = true;
      this.after(this._hiddenInputsContainer);
    }
    this._syncHiddenInputs();
  }

  _syncHiddenInputs() {
    if (this._internals) return;
    if (!this._hiddenInputsContainer) return;
    this._hiddenInputsContainer.innerHTML = '';
    if (!this.name || this.disabled) return;
    for (const val of this._selectedValues) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = this.name;
      input.value = val;
      this._hiddenInputsContainer.appendChild(input);
    }
  }

  _teardownHiddenInputs() {
    if (this._hiddenInputsContainer) {
      this._hiddenInputsContainer.remove();
      this._hiddenInputsContainer = null;
    }
  }

  _emitChange() {
    emitValueEvents(this);
  }

  _onDocumentPointer(ev) {
    const path = ev.composedPath();
    if (path.includes(this)) return;
    if (this._portalEl && path.includes(this._portalEl)) return;
    if (this.open) this.hide();
  }

  _onControlKeyDown(ev) {
    if (this.disabled) return;
    switch (ev.key) {
      case 'Enter':
      case ' ': {
        ev.preventDefault();
        if (this.open) this._selectActiveOption();
        else this.show();
        break;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        ev.preventDefault();
        if (!this.open) this.show();
        else this._moveActiveOption(ev.key === 'ArrowDown' ? 1 : -1);
        break;
      }
      case 'Home': {
        ev.preventDefault();
        if (!this.open) this.show();
        this._setActiveOption(0);
        break;
      }
      case 'End': {
        ev.preventDefault();
        if (!this.open) this.show();
        this._setActiveOption(this._visibleOptions.length - 1);
        break;
      }
      case 'Escape':
        this.hide();
        break;
      default:
        if (!ev.altKey && !ev.ctrlKey && !ev.metaKey && ev.key?.length === 1) {
          ev.preventDefault();
          this._handleTypeAhead(ev.key);
        }
        break;
    }
  }

  _onSearchKeyDown(ev) {
    if (ev.key === 'Escape') {
      this.hide();
      this._control.focus();
    } else if (ev.key === 'Tab') {
      if (this._activeOptionIndex >= 0 && !ev.shiftKey) {
        this._selectActiveOption();
      }
      if (this.open) this.hide();
    } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      this._moveActiveOption(ev.key === 'ArrowDown' ? 1 : -1);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (this._activeOptionIndex >= 0) {
        this._selectActiveOption();
      } else if (this.allowCustom) {
        this._addCustomValue(this._searchInput.value);
      }
    }
  }

  _onOptionClick(ev) {
    const li = ev.target.closest('.option');
    if (!li || li.classList.contains('disabled')) return;
    if (li.dataset.create === 'true') {
      this._addCustomValue(li.dataset.value);
      return;
    }
    const value = li.dataset.value;
    if (!value) return;
    const index = this._visibleOptions.findIndex(entry => entry.option.value === value);
    if (index >= 0) this._setActiveOption(index);
    this._toggleValue(value);
    if (!this.multiple) this.hide();
  }

  _handleFocusOut() {
    if (this._blurTimeout) clearTimeout(this._blurTimeout);
    this._blurTimeout = setTimeout(() => {
      this._blurTimeout = null;
      if (!this.isConnected) return;
      if (!this.matches(':focus-within') && !this._portalEl?.matches(':focus-within')) {
        this.hide();
      }
    }, 0);
  }

  _clearTypeahead() {
    this._typeaheadBuffer = '';
    this._typeaheadTimestamp = 0;
  }

  _resetActiveOption(options = {}) {
    if (!this.open) return;
    let idx = this._visibleOptions.findIndex((entry) => entry.option && this._selectedValues.has(entry.option.value));
    if (idx < 0) idx = this._visibleOptions.length ? 0 : -1;
    this._setActiveOption(idx, options);
  }

  _setActiveOption(index, { scrollIntoView = true } = {}) {
    this._activeOptionIndex = index;
    this._visibleOptions.forEach((entry, idx) => {
      entry.li.classList.toggle('active', idx === index);
    });
    if (scrollIntoView && index >= 0 && this._visibleOptions[index]) {
      this._visibleOptions[index].li.scrollIntoView({ block: 'nearest' });
    }
  }

  _moveActiveOption(delta) {
    if (!this.open) return;
    const len = this._visibleOptions.length;
    if (len === 0) return;
    let idx = this._activeOptionIndex;
    if (idx < 0) idx = delta > 0 ? 0 : len - 1;
    else idx = (idx + delta + len) % len;
    this._setActiveOption(idx);
  }

  _selectActiveOption() {
    if (!this.open) return;
    const entry = this._visibleOptions[this._activeOptionIndex];
    if (!entry) return;
    if (entry.isCreate) {
      this._addCustomValue(entry.createValue);
      return;
    }
    if (entry.option.disabled) return;
    this._toggleValue(entry.option.value, true);
    if (!this.multiple) this.hide();
  }

  _handleTypeAhead(char) {
    if (!char) return;
    if (!this.open) this.show();
    const now = Date.now();
    if (!this._typeaheadTimestamp || (now - this._typeaheadTimestamp) > 600) {
      this._typeaheadBuffer = '';
    }
    this._typeaheadTimestamp = now;
    this._typeaheadBuffer += char;
    this._searchInput.value = this._typeaheadBuffer;
    this._renderOptions();
    this._searchInput.focus();
    const len = this._searchInput.value.length;
    this._searchInput.setSelectionRange(len, len);
    this._resetActiveOption();
  }
}

define(TAG_NAME, OmniSelect);

export { OmniSelect };
